import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardFooter from '../components/dashboard/DashboardFooter';
import { SERVER_BASE_URL } from '../utils/api';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px 48px;
  flex: 1;
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  color: #1f2937;
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
`;

const CardTitle = styled.h2`
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 0 0 8px;
`;

const CardValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  background: #4f46e5;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
`;

const ProgressList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
`;

const ProgressItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.9rem;
`;

const EarningsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;

  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
  }

  th {
    font-size: 0.8rem;
    color: #6b7280;
    text-transform: uppercase;
  }
`;

const NavLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  a {
    color: #4f46e5;
    text-decoration: none;
    font-weight: 500;

    &:hover { text-decoration: underline; }
  }
`;

const BONUS_LABELS = {
  linear: 'Линейный',
  activity_compression: 'Компрессия',
  mentor: 'Наставник',
  leader: 'Лидерский',
  company: 'Компания',
  promo: 'Промо'
};

const Marketing = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const dashRes = await fetch(`${SERVER_BASE_URL}/api/marketing/dashboard`, { headers });
        if (!dashRes.ok) throw new Error('Не удалось загрузить маркетинг-дашборд');
        const dash = await dashRes.json();
        setData(dash);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const qual = data?.qualification;
  const missed = data?.missed;
  const earnings = data?.earnings || {};

  return (
    <PageWrapper>
      <DashboardHeader />
      <PageContainer>
        <PageTitle>Маркетинг</PageTitle>

        {loading && <Card>Загрузка...</Card>}
        {error && <Card style={{ color: '#b91c1c' }}>{error}</Card>}

        {!loading && !error && data && (
          <>
            <Grid>
              <Card>
                <CardTitle>Звание (превью)</CardTitle>
                <Badge>{qual?.title || 'Участник'}</Badge>
                {qual?.progress && !qual.progress.complete && (
                  <p style={{ marginTop: 12, color: '#6b7280', fontSize: '0.9rem' }}>
                    До {qual.progress.nextTitle}: {qual.progress.hints?.map((h) => `${h.label} ${h.current}/${h.required}`).join(' · ')}
                  </p>
                )}
              </Card>

              <Card>
                <CardTitle>Упущенное (превью)</CardTitle>
                <CardValue>{Math.round(missed?.missed_total || missed?.total || 0)} баллов</CardValue>
                <p style={{ marginTop: 8, fontSize: '0.8rem', color: '#9ca3af' }}>
                  Предварительно до закрытия периода
                </p>
              </Card>
            </Grid>

            <Grid>
              <Card>
                <CardTitle>Начисления за период</CardTitle>
                <EarningsTable>
                  <thead>
                    <tr>
                      <th>Тип бонуса</th>
                      <th>Баллы</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(BONUS_LABELS).map(([key, label]) => (
                      <tr key={key}>
                        <td>{label}</td>
                        <td>{Math.round(earnings[key] || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </EarningsTable>
              </Card>

              <Card>
                <CardTitle>Прогресс квалификации</CardTitle>
                {qual?.progress?.hints?.length ? (
                  <ProgressList>
                    {qual.progress.hints.map((h) => (
                      <ProgressItem key={h.key}>
                        <span>{h.label}</span>
                        <strong>{h.current} / {h.required}</strong>
                      </ProgressItem>
                    ))}
                  </ProgressList>
                ) : (
                  <p style={{ color: '#6b7280' }}>Максимальное звание или условия выполнены</p>
                )}
              </Card>
            </Grid>

            <Card>
              <CardTitle>Разделы</CardTitle>
              <NavLinks>
                <Link to="/dashboard/team">Команда</Link>
                <Link to="/dashboard/referral-program">История начислений</Link>
                <Link to="/dashboard">Личный кабинет</Link>
              </NavLinks>
            </Card>
          </>
        )}
      </PageContainer>
      <DashboardFooter />
    </PageWrapper>
  );
};

export default Marketing;
